import React, { useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Tabs,
  Tab,
  Badge,
  CircularProgress,
  useTheme,
  alpha,
  Paper,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  Favorite as FavoriteIcon,
  Collections as CollectionsIcon,
  ShoppingCartOutlined as ShoppingCartOutlinedIcon,
  FavoriteBorder as FavoriteBorderIcon,
} from '@mui/icons-material';
import { useUserIntents, useToggleWishlist, useToggleFavorite, useGlobalIntentCounts } from '../../hooks/useUserIntents';
import { useToggleOnePieceCardOwnership } from '../../hooks/useOnePieceCardOwnership';
import { useToggleCardOwnership } from '../../hooks/useCardOwnership';
import OnePieceCardItem from '../OnePieceSetDetails/OnePieceCardItem';
import PokemonCardItem from '../SetDetails/PokemonCardItem';
import { isChaseCard as isOnePieceChaseCard } from '../../types/onepiece';
import { isPokemonChaseCard } from '../../types/pokemon';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const BuyList: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [currentTab, setCurrentTab] = React.useState<'wishlist' | 'favorites'>('wishlist');
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Load paginated intents
  const { 
    data, 
    isLoading, 
    fetchNextPage, 
    hasNextPage, 
    isFetchingNextPage 
  } = useUserIntents(20);

  // Load global counts for badges
  const { data: counts } = useGlobalIntentCounts();

  // Infinite scroll intersection observer
  useEffect(() => {
    if (!sentinelRef.current || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Flatten all pages of items
  const allIntents = useMemo(() => {
    return data?.pages.flatMap(page => page.items) || [];
  }, [data]);

  // Filter based on tab
  const filteredIntents = useMemo(() => {
    return allIntents.filter(item => 
      currentTab === 'wishlist' ? item.isWishlist : item.isFavorite
    );
  }, [allIntents, currentTab]);

  const handleCardClick = (item: any) => {
    if (item.game === 'onepiece') {
      navigate(`/onepiece/sets/${item.setId}/cards/${item.cardId}`);
    } else {
      navigate(`/sets/${item.setId}/cards/${item.cardId}`);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          My Buy List & Favorites
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Track cards you're hunting for and mark your favorites across all games.
        </Typography>
      </Box>

      <Tabs
        value={currentTab}
        onChange={(_, newValue) => setCurrentTab(newValue)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab 
          value="wishlist" 
          label={
            <Badge badgeContent={counts?.wishlist || 0} color="info" sx={{ px: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShoppingCartIcon fontSize="small" />
                Buy List
              </Box>
            </Badge>
          } 
        />
        <Tab 
          value="favorites" 
          label={
            <Badge badgeContent={counts?.favorite || 0} color="error" sx={{ px: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FavoriteIcon fontSize="small" />
                Favorites
              </Box>
            </Badge>
          } 
        />
      </Tabs>

      {!isAuthenticated ? (
        <Paper sx={{ p: 10, textAlign: 'center', borderRadius: 4, bgcolor: alpha(theme.palette.background.paper, 0.5) }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Please sign in to view your Buy List and Favorites
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You need to be signed in to track cards you are hunting for.
          </Typography>
        </Paper>
      ) : isLoading && allIntents.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          <Grid container spacing={2}>
            {filteredIntents.map((item) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={item.intentKey}>
                {item.game === 'onepiece' ? (
                  <OnePieceCardWrapper item={item} onClick={handleCardClick} />
                ) : (
                  <PokemonCardWrapper item={item} onClick={handleCardClick} />
                )}
              </Grid>
            ))}
          </Grid>

          {/* Sentinel for infinite scroll */}
          <Box ref={sentinelRef} sx={{ height: 40, mt: 4, display: 'flex', justifyContent: 'center' }}>
            {isFetchingNextPage && <CircularProgress size={24} />}
          </Box>

          {filteredIntents.length === 0 && !isLoading && (
            <Paper sx={{ p: 10, textAlign: 'center', borderRadius: 4, bgcolor: alpha(theme.palette.background.paper, 0.5) }}>
              <Typography variant="h6" color="text.secondary">
                {currentTab === 'wishlist' 
                  ? "Your Buy List is empty. Start adding cards you want to hunt!" 
                  : "You haven't favorited any cards yet."}
              </Typography>
            </Paper>
          )}
        </Box>
      )}
    </Container>
  );
};

const OnePieceCardWrapper = ({ item, onClick }: { item: any, onClick: (i: any) => void }) => {
  const toggleWishlist = useToggleWishlist();
  const toggleFavorite = useToggleFavorite();
  const toggleOwnership = useToggleOnePieceCardOwnership();

  const card: any = {
    id: item.cardId,
    name: item.cardName,
    images: { small: item.imageUrl, large: item.imageUrl },
    setId: item.setId,
    rarity: item.rarity,
    number: item.cardNumber || item.baseCardId || item.cardId.split('_')[0],
    baseCardId: item.baseCardId,
    color: item.color,
    setName: item.setName,
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <OnePieceCardItem 
        card={card}
        isOwned={false} // We'd need to fetch ownership separately or pass it
        isFavorite={item.isFavorite}
        isWishlist={item.isWishlist}
        onCardClick={() => onClick(item)}
        onToggleOwnership={() => toggleOwnership.mutate({ cardId: card.id, setId: item.setId })}
        onToggleWishlist={() => toggleWishlist.mutate({ ...item, isWishlist: !item.isWishlist })}
        onToggleFavorite={() => toggleFavorite.mutate({ ...item, isFavorite: !item.isFavorite })}
        isToggling={toggleWishlist.isPending || toggleFavorite.isPending || toggleOwnership.isPending}
      />
      {item.isWishlist && (
        <Box sx={{ mt: 1, px: 1 }}>
          <TextField
            size="small"
            label="Quantity"
            type="number"
            value={item.wishlistQuantity}
            onChange={(e) => toggleWishlist.mutate({ ...item, wishlistQuantity: parseInt(e.target.value) || 1 })}
            InputProps={{
              startAdornment: <InputAdornment position="start">x</InputAdornment>,
            }}
            sx={{ width: '100%' }}
          />
        </Box>
      )}
    </Box>
  );
};

const PokemonCardWrapper = ({ item, onClick }: { item: any, onClick: (i: any) => void }) => {
  const toggleWishlist = useToggleWishlist();
  const toggleFavorite = useToggleFavorite();
  const toggleOwnership = useToggleCardOwnership();

  const card: any = {
    id: item.cardId,
    name: item.cardName,
    images: { small: item.imageUrl, large: item.imageUrl },
    setId: item.setId,
    rarity: item.rarity,
    number: item.cardNumber || item.baseCardId || (item.cardId.includes('-') ? item.cardId.split('-').pop() : item.cardId),
    baseCardId: item.baseCardId,
    types: item.types,
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <PokemonCardItem 
        card={card}
        isOwned={false}
        isFavorite={item.isFavorite}
        isWishlist={item.isWishlist}
        onCardClick={() => onClick(item)}
        onToggleOwnership={() => toggleOwnership.mutate(card.id)}
        onToggleWishlist={() => toggleWishlist.mutate({ ...item, isWishlist: !item.isWishlist })}
        onToggleFavorite={() => toggleFavorite.mutate({ ...item, isFavorite: !item.isFavorite })}
        isToggling={toggleWishlist.isPending || toggleFavorite.isPending || toggleOwnership.isPending}
      />
      {item.isWishlist && (
        <Box sx={{ mt: 1, px: 1 }}>
          <TextField
            size="small"
            label="Quantity"
            type="number"
            value={item.wishlistQuantity}
            onChange={(e) => toggleWishlist.mutate({ ...item, wishlistQuantity: parseInt(e.target.value) || 1 })}
            InputProps={{
              startAdornment: <InputAdornment position="start">x</InputAdornment>,
            }}
            sx={{ width: '100%' }}
          />
        </Box>
      )}
    </Box>
  );
};

export default BuyList;
